import type { Product, ProductVariant } from './api/types'

// Die Entscheidungen der Variantenauswahl, ohne React.
//
// Getrennt vom Bauteil, weil hier die Fehler wehtun: Wer eine Kombination
// anbietet, die es nicht gibt, verkauft etwas, das der Laden nicht liefern
// kann. Wer sie still weglässt, lässt den Kunden glauben, es gäbe den Pullover
// nicht in Blau — dabei gibt es nur diese eine Größe nicht in Blau.

export type Auswahl = Record<string, string>

export type WertZustand =
  /** Es gibt diese Kombination, und sie ist lieferbar. */
  | 'verfuegbar'
  /** Es gibt sie, sie ist aber gerade aus. */
  | 'ausverkauft'
  /** Diese Kombination gibt es nicht — gesperrt, aber sichtbar. */
  | 'nicht-kombinierbar'

export interface AchsenWert {
  wert: string
  zustand: WertZustand
}

export interface AchsenAnsicht {
  achse: string
  werte: AchsenWert[]
}

/** Alle Werte einer Achse, in der Reihenfolge, in der die Varianten sie nennen. */
export function werteDerAchse(achse: string, variants: ProductVariant[]): string[] {
  const gesehen: string[] = []
  for (const variant of variants) {
    const wert = variant.axes[achse]
    if (wert !== undefined && !gesehen.includes(wert)) gesehen.push(wert)
  }
  return gesehen
}

/** Die Variante, die genau zu dieser Auswahl passt — oder keine. */
export function findeVariante(product: Product, auswahl: Auswahl): ProductVariant | null {
  if (product.variantAxes.length === 0) return product.variants[0] ?? null
  return (
    product.variants.find((variant) =>
      product.variantAxes.every((achse) => variant.axes[achse] === auswahl[achse]),
    ) ?? null
  )
}

/**
 * Die Auswahl, mit der die Seite aufgeht.
 *
 * Bevorzugt eine lieferbare Variante: Wer eine Produktseite öffnet und als
 * Erstes „ausverkauft" liest, geht wieder, obwohl daneben drei Größen liegen.
 */
export function ersteAuswahl(product: Product): Auswahl {
  const variante = product.variants.find((v) => v.available) ?? product.variants[0]
  if (!variante) return {}
  const auswahl: Auswahl = {}
  for (const achse of product.variantAxes) {
    const wert = variante.axes[achse]
    if (wert !== undefined) auswahl[achse] = wert
  }
  return auswahl
}

function zustand(
  product: Product,
  auswahl: Auswahl,
  achse: string,
  wert: string,
): WertZustand {
  // Gemessen wird gegen die übrigen Achsen: „Gibt es Blau, wenn Größe S
  // bleibt?" — nicht „Gibt es Blau überhaupt?".
  const passende = product.variants.filter((variant) => {
    if (variant.axes[achse] !== wert) return false
    return product.variantAxes.every(
      (andere) => andere === achse || auswahl[andere] === undefined || variant.axes[andere] === auswahl[andere],
    )
  })

  if (passende.length === 0) return 'nicht-kombinierbar'
  return passende.some((v) => v.available) ? 'verfuegbar' : 'ausverkauft'
}

/** Alle Achsen mit ihren Werten und deren Zustand zur aktuellen Auswahl. */
export function achsenAnsicht(product: Product, auswahl: Auswahl): AchsenAnsicht[] {
  return product.variantAxes.map((achse) => ({
    achse,
    werte: werteDerAchse(achse, product.variants).map((wert) => ({
      wert,
      zustand: zustand(product, auswahl, achse, wert),
    })),
  }))
}

/**
 * Setzt einen Wert und repariert die übrigen Achsen, falls nötig.
 *
 * Der Grund ist die Sackgasse: Sperrt man nicht kombinierbare Werte einfach
 * weg, kommt jemand, der erst „S" und dann „blau" wählt, nie zu „M/blau" —
 * beide Wege sind gesperrt, und die Seite wirkt kaputt. Darum bleibt jeder
 * Wert anklickbar; die Sperre zeigt „nicht mit dieser Kombination", und der
 * Klick rückt die übrigen Achsen auf die nächstbeste vorhandene Variante.
 *
 * Behalten wird dabei so viel wie möglich: die Variante, die die meisten
 * bisherigen Werte teilt, lieferbare zuerst.
 */
export function waehle(product: Product, auswahl: Auswahl, achse: string, wert: string): Auswahl {
  const gewuenscht: Auswahl = { ...auswahl, [achse]: wert }
  if (findeVariante(product, gewuenscht)) return gewuenscht

  const kandidaten = product.variants.filter((v) => v.axes[achse] === wert)
  if (kandidaten.length === 0) return auswahl // Diesen Wert gibt es nirgends — nichts zu tun.

  let beste = kandidaten[0]!
  let bestesMass = -1
  for (const kandidat of kandidaten) {
    const treffer = product.variantAxes.reduce(
      (summe, a) => (a !== achse && kandidat.axes[a] === auswahl[a] ? summe + 1 : summe),
      0,
    )
    // Lieferbarkeit wiegt schwerer als eine zusätzlich erhaltene Achse.
    const mass = treffer + (kandidat.available ? product.variantAxes.length : 0)
    if (mass > bestesMass) {
      bestesMass = mass
      beste = kandidat
    }
  }

  const repariert: Auswahl = {}
  for (const a of product.variantAxes) {
    const w = beste.axes[a]
    if (w !== undefined) repariert[a] = w
  }
  return repariert
}

/** Preisspanne über alle Varianten — für „ab X" in Listen und Kopfzeile. */
export function preisSpanne(product: Product): { min: number; max: number } | null {
  const preise = product.variants.map((v) => v.price)
  if (preise.length === 0) return null
  return { min: Math.min(...preise), max: Math.max(...preise) }
}
